import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Video, VideoOff, Mic, MicOff, Volume2, VolumeX, X, Lock, Minimize2, Maximize2, GripHorizontal } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const RTC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

const MAX_SLOTS = 6;

// Renders a MediaStream into a <video>, controlling muted via prop (viewer audio gate).
const StreamVideo = ({ stream, muted, mirror }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);
  useEffect(() => {
    if (ref.current) ref.current.muted = !!muted;
  }, [muted]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      className="w-full h-full object-cover"
      style={mirror ? { transform: "scaleX(-1)" } : undefined}
    />
  );
};

/**
 * Room Webcam Grid — a fixed 2x3 (6-slot) grid of view-only room webcams.
 * Privacy is preserved: a broadcaster's cam only appears in another user's grid
 * after the invite/request handshake (server-enforced permissions). Audio is
 * broadcaster-only: viewers start muted and may unmute only when the broadcaster
 * has turned their master Voice ON.
 */
const RoomWebcamGrid = ({ wsRef, userId, users = [], isConnected, pendingWatch }) => {
  const [broadcasting, setBroadcasting] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [viewers, setViewers] = useState([]); // [{user_id, username}] watching MY cam
  const [remoteStreams, setRemoteStreams] = useState({}); // broadcasterId -> MediaStream
  const [broadcasterVoice, setBroadcasterVoice] = useState({}); // broadcasterId -> bool
  const [viewerMuted, setViewerMuted] = useState({}); // broadcasterId -> bool (local playback mute)
  const [collapsed, setCollapsed] = useState(false);
  const [gridHeight, setGridHeight] = useState(260);

  const { token } = useContext(AuthContext);
  const gridHeightRef = useRef(260);
  const collapsedRef = useRef(false);
  useEffect(() => { gridHeightRef.current = gridHeight; }, [gridHeight]);
  useEffect(() => { collapsedRef.current = collapsed; }, [collapsed]);

  // Load saved grid size/collapse pref so it persists between rooms/sessions.
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/users/webcam-grid-pref`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const p = r.data?.pref || {};
        if (typeof p.collapsed === "boolean") setCollapsed(p.collapsed);
        if (p.height) setGridHeight(Math.max(120, Math.min(p.height, 600)));
      })
      .catch(() => {});
  }, [token]);

  const savePref = useCallback(() => {
    if (!token) return;
    axios.put(`${API}/users/webcam-grid-pref`,
      { collapsed: collapsedRef.current, height: gridHeightRef.current },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
  }, [token]);

  const toggleCollapse = useCallback(() => {
    setCollapsed((c) => { collapsedRef.current = !c; return !c; });
    setTimeout(savePref, 0);
  }, [savePref]);

  const startResize = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = gridHeightRef.current;
    const onMove = (ev) => {
      const next = Math.max(120, Math.min(startH + (ev.clientY - startY), 600));
      gridHeightRef.current = next;
      setGridHeight(next);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      savePref();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [savePref]);

  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const peersRef = useRef({}); // peerId -> { pc, kind: 'serve'|'view' }
  const pendingIceRef = useRef({}); // peerId -> [candidates]
  const broadcastingRef = useRef(false);
  const voiceOnRef = useRef(false);
  const watchedRef = useRef(false);

  const sendWS = useCallback((obj) => {
    const ws = wsRef?.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  }, [wsRef]);

  const usernameFor = useCallback(
    (id) => users.find((u) => u.user_id === id)?.username || "User",
    [users]
  );

  const closePeer = useCallback((peerId) => {
    const p = peersRef.current[peerId];
    if (p) {
      try { p.pc.close(); } catch (_) { /* noop */ }
      delete peersRef.current[peerId];
    }
    delete pendingIceRef.current[peerId];
  }, []);

  // ---- Broadcaster: serve my stream to a permitted viewer (send-only) ----
  const serveViewer = useCallback(async (viewerId) => {
    if (!localStreamRef.current) return;
    closePeer(viewerId);
    const pc = new RTCPeerConnection(RTC_CONFIG);
    peersRef.current[viewerId] = { pc, kind: "serve" };
    localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    pc.onicecandidate = (e) => {
      if (e.candidate) sendWS({ type: "webcam_ice_candidate", target_user_id: viewerId, candidate: e.candidate });
    };
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendWS({ type: "webcam_offer", target_user_id: viewerId, offer });
    } catch (err) {
      console.error("serveViewer offer failed", err);
    }
  }, [sendWS, closePeer]);

  // ---- Start / stop my broadcast ----
  const startBroadcast = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      localStreamRef.current = stream;
      // Master Voice starts OFF: keep the mic track present but disabled.
      const audio = stream.getAudioTracks()[0];
      if (audio) audio.enabled = voiceOnRef.current;
      broadcastingRef.current = true;
      setBroadcasting(true);
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      sendWS({ type: "webcam_status", webcam_on: true, voice_on: voiceOnRef.current });
      toast.success("You are live! Viewers appear once you invite or approve them.");
      return true;
    } catch (err) {
      console.error("startBroadcast failed", err);
      if (err.name === "NotAllowedError") toast.error("Camera permission denied.");
      else if (err.name === "NotFoundError") toast.error("No camera found.");
      else toast.error("Failed to access camera.");
      return false;
    }
  }, [sendWS]);

  const stopBroadcast = useCallback(() => {
    Object.keys(peersRef.current).forEach((pid) => {
      if (peersRef.current[pid].kind === "serve") closePeer(pid);
    });
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    broadcastingRef.current = false;
    setBroadcasting(false);
    setViewers([]);
    setVoiceOn(false);
    voiceOnRef.current = false;
    sendWS({ type: "webcam_status", webcam_on: false });
  }, [sendWS, closePeer]);

  const toggleVoice = useCallback(() => {
    const next = !voiceOnRef.current;
    voiceOnRef.current = next;
    setVoiceOn(next);
    const audio = localStreamRef.current?.getAudioTracks()[0];
    if (audio) audio.enabled = next;
    sendWS({ type: "webcam_status", webcam_on: true, voice_on: next });
    toast.info(next ? "Voice ON — viewers can now hear you" : "Voice OFF — you are muted to viewers");
  }, [sendWS]);

  const kickViewer = useCallback((viewerId) => {
    sendWS({ type: "webcam_viewer_removed", target_user_id: viewerId });
    closePeer(viewerId);
    setViewers((prev) => prev.filter((v) => v.user_id !== viewerId));
  }, [sendWS, closePeer]);

  // ---- Viewer: request / start viewing a broadcaster ----
  const totalSlots = useCallback(
    () => Object.keys(remoteStreams).length + (broadcastingRef.current ? 1 : 0),
    [remoteStreams]
  );

  const viewBroadcaster = useCallback((broadcasterId) => {
    if (!broadcasterId || broadcasterId === userId) return;
    if (peersRef.current[broadcasterId]) return; // already viewing
    if (Object.keys(remoteStreams).length + (broadcastingRef.current ? 1 : 0) >= MAX_SLOTS) {
      toast.error("Webcam grid is full (6 slots). Close one to view another.");
      return;
    }
    setViewerMuted((m) => ({ ...m, [broadcasterId]: true })); // start muted
    sendWS({ type: "webcam_view_start", broadcaster_id: broadcasterId });
  }, [sendWS, userId, remoteStreams]);

  const requestToView = useCallback((broadcasterId) => {
    sendWS({ type: "webcam_request", target_user_id: broadcasterId });
    toast.info(`Requested to view ${usernameFor(broadcasterId)}'s cam`);
  }, [sendWS, usernameFor]);

  const stopViewing = useCallback((broadcasterId) => {
    sendWS({ type: "webcam_view_stop", broadcaster_id: broadcasterId });
    closePeer(broadcasterId);
    setRemoteStreams((s) => { const n = { ...s }; delete n[broadcasterId]; return n; });
  }, [sendWS, closePeer]);

  // ---- Viewer: handle an incoming offer from a broadcaster (recv-only) ----
  const handleOffer = useCallback(async (fromId, offer) => {
    closePeer(fromId);
    const pc = new RTCPeerConnection(RTC_CONFIG);
    peersRef.current[fromId] = { pc, kind: "view" };
    pc.ontrack = (e) => {
      setRemoteStreams((s) => ({ ...s, [fromId]: e.streams[0] }));
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) sendWS({ type: "webcam_ice_candidate", target_user_id: fromId, candidate: e.candidate });
    };
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      (pendingIceRef.current[fromId] || []).forEach((c) => pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {}));
      pendingIceRef.current[fromId] = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendWS({ type: "webcam_answer", target_user_id: fromId, answer });
    } catch (err) {
      console.error("handleOffer failed", err);
    }
  }, [sendWS, closePeer]);

  const handleAnswer = useCallback(async (fromId, answer) => {
    const p = peersRef.current[fromId];
    if (!p) return;
    try {
      await p.pc.setRemoteDescription(new RTCSessionDescription(answer));
      (pendingIceRef.current[fromId] || []).forEach((c) => p.pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {}));
      pendingIceRef.current[fromId] = [];
    } catch (err) {
      console.error("handleAnswer failed", err);
    }
  }, []);

  const handleIce = useCallback(async (fromId, candidate) => {
    const p = peersRef.current[fromId];
    if (p && p.pc.remoteDescription) {
      try { await p.pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (_) { /* noop */ }
    } else {
      pendingIceRef.current[fromId] = pendingIceRef.current[fromId] || [];
      pendingIceRef.current[fromId].push(candidate);
    }
  }, []);

  // ---- Central WebSocket signaling listener ----
  useEffect(() => {
    const ws = wsRef?.current;
    if (!ws) return;
    const onMessage = async (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch (_) { return; }
      switch (data.type) {
        case "webcam_offer":
          handleOffer(data.from_user_id, data.offer);
          break;
        case "webcam_answer":
          handleAnswer(data.from_user_id, data.answer);
          break;
        case "webcam_ice_candidate":
          handleIce(data.from_user_id, data.candidate);
          break;
        case "webcam_viewer_joined": {
          const vid = data.viewer_id;
          setViewers((prev) => (prev.find((v) => v.user_id === vid) ? prev : [...prev, { user_id: vid, username: data.viewer_username }]));
          if (broadcastingRef.current) {
            serveViewer(vid);
          } else {
            // Approved a viewer while my cam was off — auto go live, then serve.
            const ok = await startBroadcast();
            if (ok) serveViewer(vid);
          }
          break;
        }
        case "webcam_viewer_left":
          closePeer(data.viewer_id);
          setViewers((prev) => prev.filter((v) => v.user_id !== data.viewer_id));
          break;
        case "webcam_granted":
          toast.success(`${data.from_username} let you view their cam`);
          viewBroadcaster(data.from_user_id);
          break;
        case "webcam_invited":
          toast.info(`${data.broadcaster_username} invited you to their cam`);
          viewBroadcaster(data.broadcaster_id);
          break;
        case "webcam_access_denied":
          toast.error(data.reason || "This webcam is locked.");
          closePeer(data.broadcaster_id);
          setRemoteStreams((s) => { const n = { ...s }; delete n[data.broadcaster_id]; return n; });
          break;
        case "webcam_access_revoked":
          toast.error(`${data.broadcaster_username || "The broadcaster"} stopped sharing with you`);
          closePeer(data.broadcaster_id);
          setRemoteStreams((s) => { const n = { ...s }; delete n[data.broadcaster_id]; return n; });
          break;
        case "webcam_end":
          closePeer(data.from_user_id);
          setRemoteStreams((s) => { const n = { ...s }; delete n[data.from_user_id]; return n; });
          break;
        case "user_webcam_status":
          setBroadcasterVoice((bv) => ({ ...bv, [data.user_id]: !!data.voice_on }));
          if (!data.webcam_on) {
            closePeer(data.user_id);
            setRemoteStreams((s) => { const n = { ...s }; delete n[data.user_id]; return n; });
          }
          break;
        default:
          break;
      }
    };
    ws.addEventListener("message", onMessage);
    return () => ws.removeEventListener("message", onMessage);
  }, [wsRef, isConnected, handleOffer, handleAnswer, handleIce, serveViewer, startBroadcast, closePeer, viewBroadcaster]);

  // Auto-view when arriving via an IM "Watch" link (?watch=<broadcaster_id>).
  useEffect(() => {
    if (!isConnected || watchedRef.current || !pendingWatch) return;
    if (users.some((u) => u.user_id === pendingWatch)) {
      watchedRef.current = true;
      viewBroadcaster(pendingWatch);
    }
  }, [isConnected, pendingWatch, users, viewBroadcaster]);

  // Attach the local broadcast stream once the tile's <video> has actually rendered.
  useEffect(() => {
    if (broadcasting && localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [broadcasting]);

  // Cleanup on unmount / leaving the room.
  useEffect(() => {
    return () => {
      Object.keys(peersRef.current).forEach((pid) => {
        try { peersRef.current[pid].pc.close(); } catch (_) { /* noop */ }
      });
      peersRef.current = {};
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
    };
  }, []);

  // Broadcasters I could watch but haven't been granted yet (locked).
  const lockedBroadcasters = users.filter(
    (u) => u.webcam_on && u.user_id !== userId && !remoteStreams[u.user_id] && !peersRef.current[u.user_id]
  );

  // Build the grid slots dynamically: my cam first (if live), then remote streams.
  // No empty padding — the grid grows from 1 up to a max of 6 active tiles.
  const tiles = [];
  if (broadcasting) tiles.push({ kind: "local" });
  Object.keys(remoteStreams).forEach((bId) => tiles.push({ kind: "remote", bId }));
  const shownTiles = tiles.slice(0, MAX_SLOTS);
  const gridColsClass = shownTiles.length <= 1 ? "grid-cols-1" : "grid-cols-2";

  return (
    <div className="flex flex-col h-full" data-testid="room-webcam-grid">
      {/* Header + collapse/expand */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold text-[#606060] flex items-center gap-1">
          <Video className="w-3 h-3" /> Room Cams
        </span>
        <button
          className="retro-button p-0.5"
          onClick={toggleCollapse}
          title={collapsed ? "Expand grid" : "Collapse to names only"}
          data-testid="grid-collapse-btn"
        >
          {collapsed ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
        </button>
      </div>

      {/* Controls */}
      <div className="mb-2 flex gap-1">
        {!broadcasting ? (
          <button
            className="retro-button flex-1 py-1.5 bg-green-100 flex items-center justify-center gap-2 text-xs"
            onClick={startBroadcast}
            data-testid="start-broadcast-btn"
          >
            <Video className="w-4 h-4 text-green-600" /> Broadcast My Cam
          </button>
        ) : (
          <>
            <button
              className={`retro-button flex-1 py-1.5 flex items-center justify-center gap-1 text-xs ${voiceOn ? "bg-blue-200" : "bg-gray-200"}`}
              onClick={toggleVoice}
              data-testid="voice-toggle-btn"
              title="Master voice — when ON, viewers can hear you"
            >
              {voiceOn ? <Mic className="w-4 h-4 text-blue-700" /> : <MicOff className="w-4 h-4 text-gray-600" />}
              {voiceOn ? "Voice ON" : "Voice OFF"}
            </button>
            <button
              className="retro-button flex-1 py-1.5 bg-red-200 flex items-center justify-center gap-1 text-xs"
              onClick={stopBroadcast}
              data-testid="stop-broadcast-btn"
            >
              <VideoOff className="w-4 h-4 text-red-600" /> Stop
            </button>
          </>
        )}
      </div>

      {/* Dynamic grid (1 up to 6 active tiles, max 2 columns) — resizable + collapsible */}
      {!collapsed && (
        <div className="overflow-y-auto flex-shrink-0" style={{ maxHeight: gridHeight }} data-testid="webcam-grid-scroll">
      {shownTiles.length === 0 ? (
        <div className="retro-inset p-3 text-center text-[10px] text-[#606060]" data-testid="webcam-grid-empty">
          No cams active. Broadcast yours, or request a locked cam below.
        </div>
      ) : (
        <div className={`grid ${gridColsClass} gap-1 flex-shrink-0`} data-testid="webcam-grid-tiles">
          {shownTiles.map((tile) => {
            if (tile.kind === "local") {
              return (
                <div key="local" className="retro-inset relative bg-black aspect-video overflow-hidden" data-testid="my-cam-tile">
                  <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
                  <span className="absolute top-1 left-1 text-[9px] text-white bg-red-600 px-1 rounded flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE
                  </span>
                  <span className="absolute bottom-0 left-0 right-0 text-[9px] text-white bg-black/60 px-1 truncate">
                    You {voiceOn ? "🔊" : "🔇"} · {viewers.length} watching
                  </span>
                </div>
              );
            }
            const bId = tile.bId;
            const canHear = !!broadcasterVoice[bId];
            const muted = !canHear || (viewerMuted[bId] ?? true);
            return (
              <div key={bId} className="retro-inset relative bg-black aspect-video overflow-hidden" data-testid={`cam-tile-${bId}`}>
                <StreamVideo stream={remoteStreams[bId]} muted={muted} />
                <span className="absolute bottom-0 left-0 right-0 text-[9px] text-white bg-black/60 px-1 truncate">
                  {usernameFor(bId)}
                </span>
                <div className="absolute top-0.5 right-0.5 flex gap-0.5">
                  <button
                    className={`text-white bg-black/50 rounded p-0.5 ${!canHear ? "opacity-40 cursor-not-allowed" : "hover:bg-black/80"}`}
                    onClick={() => canHear && setViewerMuted((m) => ({ ...m, [bId]: !(m[bId] ?? true) }))}
                    disabled={!canHear}
                    title={canHear ? (muted ? "Unmute" : "Mute") : "Broadcaster voice is off"}
                    data-testid={`cam-mute-${bId}`}
                  >
                    {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                  </button>
                  <button
                    className="text-white bg-black/50 rounded p-0.5 hover:bg-red-600"
                    onClick={() => stopViewing(bId)}
                    title="Close this cam"
                    data-testid={`cam-close-${bId}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </div>
      )}

      {/* Drag handle to resize the grid height */}
      {!collapsed && (
        <div
          className="flex items-center justify-center h-3 cursor-ns-resize hover:bg-gray-300 rounded-sm mt-0.5"
          onPointerDown={startResize}
          title="Drag to resize the grid"
          data-testid="grid-resize-handle"
        >
          <GripHorizontal className="w-4 h-4 text-gray-500" />
        </div>
      )}

      {/* Collapsed = just the broadcasters' names */}
      {collapsed && (
        <div className="retro-inset p-1 space-y-0.5" data-testid="grid-collapsed-names">
          {broadcasting && (
            <div className="flex items-center gap-1 text-[10px]">
              <span className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse" /> You (LIVE) · {viewers.length} watching
            </div>
          )}
          {Object.keys(remoteStreams).map((bId) => (
            <div key={bId} className="flex items-center gap-1 text-[10px] truncate">
              <span className="w-1.5 h-1.5 bg-red-600 rounded-full" /> {usernameFor(bId)}
            </div>
          ))}
          {!broadcasting && Object.keys(remoteStreams).length === 0 && (
            <div className="text-[10px] text-[#606060]">No cams active</div>
          )}
        </div>
      )}

      {/* My viewers (broadcaster moderation) */}
      {broadcasting && viewers.length > 0 && (
        <div className="retro-inset p-1 mt-2 max-h-24 overflow-y-auto">
          <p className="text-[10px] font-bold mb-0.5">Viewers ({viewers.length}):</p>
          {viewers.map((v) => (
            <div key={v.user_id} className="flex items-center justify-between text-[10px] bg-white px-1 py-0.5">
              <span className="truncate">{v.username}</span>
              <button className="text-red-600 hover:bg-red-100 px-1" onClick={() => kickViewer(v.user_id)} data-testid={`kick-viewer-${v.user_id}`} title="Kick viewer">✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Locked broadcasters you can request */}
      <div className="flex-1 overflow-y-auto mt-2">
        {lockedBroadcasters.length > 0 && (
          <>
            <p className="text-[10px] font-bold text-[#808080] mb-1">Locked cams (request to view):</p>
            <div className="space-y-1">
              {lockedBroadcasters.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between text-[10px] bg-white/70 px-1 py-0.5 rounded">
                  <span className="flex items-center gap-1 truncate"><Lock className="w-3 h-3 text-[#808080]" /> {u.username}</span>
                  <button className="retro-button text-[9px] px-1.5 py-0.5" onClick={() => requestToView(u.user_id)} data-testid={`request-cam-${u.user_id}`}>Request</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RoomWebcamGrid;
